if (!customElements.get('theme-newsletter-form')) {
  class ThemeNewsletterForm extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this._controller = new AbortController();
      const { signal } = this._controller;

      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.emailRow = this.emailInput?.closest('.newsletter-form__row');
      this.submitButton = this.querySelector('[data-newsletter-submit]');
      this.spinner = this.querySelector('.loading__spinner');
      this.statusEl = this.querySelector('[data-newsletter-status]');
      this.thanksEl = this.querySelector('[data-newsletter-thanks]');
      this.gdprWrapper = this.querySelector('.newsletter-form__gdpr');
      this.gdprCheckbox = this.gdprWrapper?.querySelector('.form-checkbox__input');

      this.integration = this.dataset.integration || 'shopify';
      this.klaviyoList = this.dataset.klaviyoList || '';
      this.mailchimpUrl = this.dataset.mailchimpUrl || '';
      this.doubleOptIn = this.dataset.doubleOptIn === 'true';
      this.postSignupAction = this.dataset.postSignup || 'thanks_message';
      this.redirectUrl = this.dataset.redirectUrl || '';

      this.ensureFieldError();

      // The Shopify customer form keeps native constraint validation ON.
      // Shopify's own bot-protection script intercepts the 'submit' event to
      // run its CAPTCHA challenge and, once solved, resubmits the form itself
      // via form.submit() — which by spec never fires the 'submit' event, so
      // a listener-based check here can be silently bypassed. Native
      // constraint validation runs a layer below any script (ours or
      // Shopify's) and blocks an invalid submission before either ever sees
      // it. The 'invalid' listeners below swap the native tooltip for our
      // own inline message without weakening that guarantee. klaviyo/
      // mailchimp fully intercept submission via fetch, so they keep
      // novalidate — there's no native submission to race there.
      if (this.form && this.integration !== 'shopify') this.form.noValidate = true;
      this.form?.addEventListener('submit', this.handleSubmit, { signal });
      this.emailInput?.addEventListener('input', () => this.clearFieldError(), { signal });
      this.emailInput?.addEventListener('invalid', (event) => this.handleInvalidEmail(event), { signal });
      this.gdprCheckbox?.addEventListener('change', () => this.clearCheckboxError(), { signal });
      this.gdprCheckbox?.addEventListener('invalid', (event) => this.handleInvalidCheckbox(event), { signal });

      // The button ships `disabled` in the server-rendered markup so a click
      // landing before this script has finished attaching handleSubmit can't
      // slip through as an un-validated native submission. Only re-enable it
      // once validation is actually wired up.
      this.submitButton?.removeAttribute('disabled');
    }

    disconnectedCallback() {
      this._controller?.abort();
    }

    ensureFieldError() {
      this.fieldErrorEl = this.querySelector('[data-newsletter-error]');
    }

    async handleSubmit(event) {
      if (this.isLoading) {
        event.preventDefault();
        return;
      }

      const email = this.emailInput?.value.trim();
      if (!email) {
        event.preventDefault();
        this.handleError(theme.locales.newsletter.emailRequired, { silent: true });
        return;
      }
      if (!this.isValidEmail(email)) {
        event.preventDefault();
        this.handleError(theme.locales.newsletter.emailInvalid, { silent: true });
        return;
      }
      this.clearFieldError();

      if (this.gdprCheckbox?.required && !this.gdprCheckbox.checked) {
        event.preventDefault();
        this.showCheckboxError();
        return;
      }
      this.clearCheckboxError();

      theme.pubsub?.publish(theme.pubsub.events.FORM_SUBMIT, { integration: this.integration });

      // The Shopify customer form is intentionally NOT intercepted: it redirects
      // on success and re-renders inline with form.errors on validation failure,
      // a flow a fetch() call can't reliably reproduce (redirects lose the
      // posted_successfully? context, and stores with bot-protection enabled
      // require a real navigation to complete the challenge). Submitting
      // natively lets Shopify and the existing Liquid fallback handle it.
      if (this.integration === 'shopify') {
        this.setLoading(true);
        return;
      }

      event.preventDefault();
      this.setLoading(true);

      try {
        if (this.integration === 'klaviyo') {
          await this.submitKlaviyo(email);
        } else if (this.integration === 'mailchimp') {
          await this.submitMailchimp(email);
        }
      } finally {
        this.setLoading(false);
      }
    }

    isValidEmail(email) {
      return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    handleInvalidEmail(event) {
      event.preventDefault();
      const email = this.emailInput?.value.trim();
      this.showFieldError(!email ? theme.locales.newsletter.emailRequired : theme.locales.newsletter.emailInvalid);
    }

    handleInvalidCheckbox(event) {
      event.preventDefault();
      this.showCheckboxError();
    }

    async submitKlaviyo(email) {
      if (!this.klaviyoList) {
        this.handleError(theme.locales.newsletter.error);
        return;
      }

      try {
        const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe_external', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ g: this.klaviyoList, email }).toString(),
        });
        const data = await response.json().catch(() => null);

        if (response.ok && data?.success !== false) {
          this.handleSuccess();
          return;
        }

        const message = this.isAlreadySubscribed(data) ? theme.locales.newsletter.alreadySubscribed : theme.locales.newsletter.error;
        this.handleError(message);
      } catch (error) {
        this.handleError(theme.locales.form.error.network);
      }
    }

    isAlreadySubscribed(data) {
      const message = JSON.stringify(data?.errors || data?.message || '').toLowerCase();
      return message.includes('already') || message.includes('suscri') || message.includes('assinad');
    }

    async submitMailchimp(email) {
      if (!this.mailchimpUrl) {
        this.handleError(theme.locales.newsletter.error);
        return;
      }

      const formData = new FormData();
      formData.append('EMAIL', email);

      try {
        // Classic Mailchimp embedded forms don't support CORS: the response is opaque
        // under mode "no-cors" and cannot be inspected. A completed request is the
        // only signal available, so it's treated as an optimistic success.
        await fetch(this.mailchimpUrl, {
          method: 'POST',
          mode: 'no-cors',
          body: formData,
        });
        this.handleSuccess();
      } catch (error) {
        this.handleError(theme.locales.form.error.network);
      }
    }

    handleSuccess() {
      const message = this.decodeEntities(
        this.doubleOptIn ? theme.locales.newsletter.doubleOptInMessage : theme.locales.newsletter.success
      );

      theme.toast?.show({ type: 'success', message });
      this.announce(message);
      theme.pubsub?.publish(theme.pubsub.events.FORM_SUCCESS, { integration: this.integration });

      if (this.postSignupAction === 'redirect' && this.redirectUrl) {
        window.location.href = this.redirectUrl;
        return;
      }

      this.showThanks(message);
    }

    handleError(message, { silent = false } = {}) {
      message = this.decodeEntities(message);
      if (!silent) {
        theme.toast?.show({ type: 'error', message });
        theme.pubsub?.publish(theme.pubsub.events.FORM_ERROR, { integration: this.integration, error: message });
      }

      this.announce(message);
      this.showFieldError(message);
    }

    showThanks(message) {
      if (!this.thanksEl) return;
      this.thanksEl.textContent = message;
      this.thanksEl.hidden = false;
    }

    showFieldError(message) {
      if (this.fieldErrorEl) {
        this.fieldErrorEl.textContent = message;
        this.fieldErrorEl.hidden = false;
      }
      this.emailInput?.setAttribute('aria-invalid', 'true');
      this.emailRow?.classList.add('newsletter-form__row--error');
    }

    clearFieldError() {
      if (this.fieldErrorEl) {
        this.fieldErrorEl.hidden = true;
        this.fieldErrorEl.textContent = '';
      }
      this.emailInput?.removeAttribute('aria-invalid');
      this.emailRow?.classList.remove('newsletter-form__row--error');
    }

    showCheckboxError() {
      this.gdprWrapper?.classList.add('form-checkbox--error');
      this.gdprCheckbox?.setAttribute('aria-invalid', 'true');
    }

    clearCheckboxError() {
      this.gdprWrapper?.classList.remove('form-checkbox--error');
      this.gdprCheckbox?.removeAttribute('aria-invalid');
    }

    announce(message) {
      if (this.statusEl) this.statusEl.textContent = message;
    }

    decodeEntities(str) {
      if (!str || str.indexOf('&') === -1) return str;
      const el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.submitButton?.toggleAttribute('disabled', isLoading);
      this.submitButton?.setAttribute('aria-busy', String(isLoading));
      this.spinner?.classList.toggle('hidden', !isLoading);
    }
  }

  customElements.define('theme-newsletter-form', ThemeNewsletterForm);
}
